import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer" // Assuming standard Rails ActionCable setup

export default class extends Controller {
  static targets = [
    "chatHistory",
    "input",
    "sendButton",
    "stopButton",
    "loading",
    "welcomeMessage",
    "modelSwitcher",
    "fileInput",
    "imagePreview",
    "imagePreviewImg",
    "creditsUsed",
    "assetManagerModal",
    "assetGrid",
    "assetEmpty",
    "assetUploadInput"
  ]

  static values = {
    pageContainerId: String,
    pageLabel: String,
    currentUserId: String,
    revisionHistoryUrl: String,
    stopUrl: String,
    upgradeUrl: String,
    assetsUrl: String
  }

  connect() {
    this.isLocalhost = this.isLocalhostEnvironment()
    this.isFirstMessage = true
    this.conversationHistory = []
    this.currentConversationId = null
    this.streamingTextElement = null
    this.chatStartAbortController = null
    this.stopAbortController = null
    this.activeStreamRequestId = null
    this.ignoreIncomingStream = false
    this.isStopping = false
    this.currentAIModel = 'claude' // Default
    this.messageIndexCounter = 0
    this.revertCheckpoints = []
    this.lastKnownEditorHtml = this.getCurrentEditorHtml()
    this.resetRevertTimeline()
    this.element.__stimulusController = this

    // Initialize stored conversation if available
    this.initializeConversation()

    // Auto-resize input
    this.inputTarget.addEventListener('input', () => {
      this.inputTarget.style.height = 'auto'
      this.inputTarget.style.height = Math.min(this.inputTarget.scrollHeight, 120) + 'px'
    })
  }

  disconnect() {
    if (this.element && this.element.__stimulusController === this) {
      delete this.element.__stimulusController
    }

    this.abortPendingChatRequest()
    this.abortPendingStopRequest()
    this.unsubscribeFromStream()
    this.resetActiveStreamState()
    this.clearTimeoutDetection()
  }

  async initializeConversation() {
    await this.loadConversations()

    const storedId = sessionStorage.getItem(`current_conversation_${this.pageContainerIdValue}`)

    if (storedId && this.conversations.find(c => c.id == storedId)) {
      this.currentConversationId = parseInt(storedId)
      await this.loadConversation(this.currentConversationId)
    } else if (this.conversations.length > 0) {
      this.currentConversationId = this.conversations[0].id
      await this.loadConversation(this.currentConversationId)
    } else {
      await this.createNewConversation()
    }

    this.updateModelButton()
  }

  async loadConversations() {
    try {
      const response = await fetch(`/page_containers/${this.pageContainerIdValue}/code_assistant_conversations`, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) throw new Error('Failed to load conversations')

      const data = await response.json()
      this.conversations = data.conversations || []
    } catch (error) {
      console.error("Error loading conversations:", error)
      this.conversations = []
    }
  }

  async createNewConversation() {
    const btn = document.getElementById('new-conversation-btn')
    const originalHtml = btn ? btn.innerHTML : ''

    if (btn) {
      btn.disabled = true
      btn.innerHTML = `
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        Creating...
      `
    }

    this.addSystemMessage('Creating new conversation...')

    try {
      const response = await fetch(`/page_containers/${this.pageContainerIdValue}/code_assistant_conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          provider: this.currentAIModel
        })
      })

      if (!response.ok) throw new Error('Failed to create conversation')

      const data = await response.json()
      this.currentConversationId = data.id

      sessionStorage.setItem(`current_conversation_${this.pageContainerIdValue}`, this.currentConversationId)

      // Clear chat history
      if (this.hasChatHistoryTarget) {
        this.chatHistoryTarget.innerHTML = `
          <div class="ai-welcome-message" data-page-editor-agent-target="welcomeMessage">
            <div class="ai-message-bubble assistant">
              Describe the changes you want made to the design of this page and I'll implement them for you.
            </div>
          </div>
        `
      }
      this.conversationHistory = []
      this.isFirstMessage = true
      this.resetRevertTimeline()

      await this.loadConversations()

      this.showNotification('✓ New conversation started', 'success')
    } catch (error) {
      this.showNotification('✗ Failed to create conversation', 'error')
      this.removeLastSystemMessage()
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = originalHtml
      }
    }
  }

  async loadConversation(conversationId, showLoading = false) {
    if (showLoading) {
      this.addSystemMessage('Loading conversation...')
    }

    try {
      const response = await fetch(`/page_containers/${this.pageContainerIdValue}/code_assistant_conversations/${conversationId}`, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) throw new Error('Failed to load conversation')

      const data = await response.json()
      this.currentConversationId = data.conversation.id

      sessionStorage.setItem(`current_conversation_${this.pageContainerIdValue}`, this.currentConversationId)

      const conversationProvider = this.normalizeModel(data.conversation.provider || 'claude')
      if (conversationProvider !== this.currentAIModel) {
        this.currentAIModel = conversationProvider
        this.updateModelButton()
      }

      // Rebuild chat history
      if (this.hasChatHistoryTarget) {
        this.chatHistoryTarget.innerHTML = ''
      }
      this.conversationHistory = []
      this.isFirstMessage = data.messages.length === 0
      this.resetRevertTimeline()

      data.messages.forEach(msg => {
        if (msg.role === 'user' || msg.role === 'assistant') {
          this.addMessage(msg.role, msg.content)
          this.conversationHistory.push({
            role: msg.role,
            content: msg.content
          })
        } else if (msg.role === 'tool') {
          this.addToolCallMessage(msg.name || msg.tool_name, msg.arguments || msg.tool_arguments)
          this.conversationHistory.push({
            role: 'tool',
            name: msg.name || msg.tool_name,
            arguments: msg.arguments || msg.tool_arguments
          })
        }
      })

      if (data.messages.length === 0 && this.hasChatHistoryTarget) {
        this.chatHistoryTarget.innerHTML = `
          <div class="ai-welcome-message" data-page-editor-agent-target="welcomeMessage">
            <div class="ai-message-bubble assistant">
              Describe the changes you want made to the design of this page and I'll implement them for you.
            </div>
          </div>
        `
      }

      if (showLoading) {
        this.showNotification('✓ Conversation loaded', 'success')
      }
    } catch (error) {
      this.showNotification('✗ Failed to load conversation', 'error')
      if (showLoading) {
        this.removeLastSystemMessage()
      }
    }
  }

  updateModelButton() {
    if (!this.hasModelSwitcherTarget) return

    const btn = this.modelSwitcherTarget
    this.currentAIModel = this.normalizeModel(this.currentAIModel)

    if (this.currentAIModel === 'openai') {
      btn.textContent = 'Mode: Fast'
      btn.title = 'Powered by the fast-mode backend'
      btn.style.background = 'linear-gradient(135deg, #10a37f 0%, #1a7f64 100%)'
    } else if (this.currentAIModel === 'test') {
      btn.textContent = 'Mode: Test'
      btn.title = 'Localhost simulation mode (randomized output)'
      btn.style.background = 'linear-gradient(135deg, #ff8a00 0%, #e05200 100%)'
    } else {
      btn.textContent = 'Mode: Smart'
      btn.title = 'Powered by Claude Sonnet 4.5'
      btn.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }

    btn.dataset.currentModel = this.currentAIModel
  }

  isLocalhostEnvironment() {
    const host = window.location.hostname
    return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]'
  }

  availableModels() {
    const modes = ['claude', 'openai']
    if (this.isLocalhost) modes.push('test')
    return modes
  }

  normalizeModel(model) {
    const normalized = String(model || 'claude').toLowerCase()
    return this.availableModels().includes(normalized) ? normalized : 'claude'
  }

  modelLabel(model) {
    if (model === 'openai') return 'Fast'
    if (model === 'test') return 'Test'
    return 'Smart'
  }

  randomString(length = 12) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let output = ''
    for (let i = 0; i < length; i += 1) {
      output += chars[Math.floor(Math.random() * chars.length)]
    }
    return output
  }

  randomBetween(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  splitIntoRandomChunks(text) {
    const chunks = []
    let index = 0

    while (index < text.length) {
      const chunkSize = this.randomBetween(1, 6)
      chunks.push(text.slice(index, index + chunkSize))
      index += chunkSize
    }

    return chunks
  }

  buildTestModeResponse(userMessage) {
    const promptSnippet = (userMessage || '').replace(/\s+/g, ' ').trim().slice(0, 120)
    const sectionId = `section_${this.randomString(8)}`
    const styleId = `style_${this.randomString(8)}`
    const checksum = `${this.randomString(12)}-${this.randomString(12)}`
    const tokens = Array.from({ length: 7 }, () => this.randomString(this.randomBetween(8, 16)))

    return [
      'Test Mode (localhost simulation)\n',
      `request_id: test_${this.randomString(10)}\n`,
      `run_id: sim_${this.randomString(10)}\n`,
      `seed_checksum: ${checksum}\n`,
      `echo_prompt: ${promptSnippet || '[empty prompt]'}\n`,
      '\n',
      'Simulated generation output:\n',
      `- created_block: ${sectionId}\n`,
      `- updated_style: ${styleId}\n`,
      `- diff_ref: diff_${this.randomString(10)}\n`,
      '\n',
      `stream_payload: ${tokens.join(' ')}\n`,
      `final_trace: ${this.randomString(14)} ${this.randomString(14)}\n`
    ].join('')
  }

  async simulateTestModeResponse(userMessage) {
    this.setEditorReadOnly(true)
    await this.delay(this.randomBetween(150, 350))
    this.removeLastSystemMessage()

    const responseText = this.buildTestModeResponse(userMessage)
    const chunks = this.splitIntoRandomChunks(responseText)

    for (const chunk of chunks) {
      if (this.isStopping || this.ignoreIncomingStream) return
      this.handleToken(chunk, { countCredits: false })
      await this.delay(this.randomBetween(10, 35))
    }

    if (this.isStopping || this.ignoreIncomingStream) return
    this.handleDone({})
  }

  copyDebugInfo() {
    const btn = document.getElementById('copy-conversation-btn')

    const hasMessages = this.conversationHistory.length > 0 ||
      this.chatHistoryTarget.querySelectorAll('.ai-message-group:not(.system)').length > 0

    if (!hasMessages) {
      this.showNotification('⚠️ No conversation to copy yet. Start chatting first!', 'warning')
      return
    }

    this.addSystemMessage('📋 Copying conversation...')

    if (btn) {
      btn.disabled = true
      btn.style.opacity = '0.5'
    }

    let formattedConversation = '# Code Assistant Debug Log\n\n'
    formattedConversation += `**Page Container ID:** ${this.pageContainerIdValue}\n`
    formattedConversation += `**Page Label:** ${this.pageLabelValue}\n`
    formattedConversation += `**Timestamp:** ${new Date().toISOString()}\n\n`
    formattedConversation += '---\n\n'
    formattedConversation += '## Conversation History\n\n'

    // Iterate through message groups
    this.chatHistoryTarget.querySelectorAll('.ai-message-group, .ai-welcome-message').forEach(group => {
      if (group.classList.contains('ai-welcome-message') && this.conversationHistory.length > 0) return
      if (group.classList.contains('system')) return

      let role = 'assistant'
      if (group.classList.contains('user')) role = 'user'
      else if (group.classList.contains('tool')) role = 'tool'

      const bubble = group.querySelector('.ai-message-bubble')

      if (role === 'tool') {
        const toolDescription = bubble.querySelector('.tool-name div:first-child').textContent.trim()
        // Reconstructing args isn't easy from DOM, but we have it in conversationHistory if we matched indices.
        // For simplicity, just output description.
        formattedConversation += `### ${toolDescription}\n\n`
      } else {
        const messageText = bubble.querySelector('.message-text').textContent.trim()
        const roleLabel = role === 'user' ? '👤 User' : '🤖 Assistant'
        formattedConversation += `### ${roleLabel}\n\n`
        formattedConversation += messageText + '\n\n'
      }
    })

    formattedConversation += '---\n\n'
    formattedConversation += '## Current Editor State\n\n'

    const currentHtml = window.monacoEditor ?
      window.monacoEditor.getValue() :
      document.getElementById('html_content_textarea').value

    const htmlPreview = currentHtml.substring(0, 500)
    formattedConversation += '```html\n'
    formattedConversation += htmlPreview
    if (currentHtml.length > 500) {
      formattedConversation += '\n... (truncated, ' + currentHtml.length + ' characters total)'
    }
    formattedConversation += '\n```\n\n'

    formattedConversation += '---\n\n'
    formattedConversation += '## Metadata\n\n'
    formattedConversation += `- **Total Messages:** ${this.conversationHistory.length}\n`

    navigator.clipboard.writeText(formattedConversation)
      .then(() => {
        this.removeLastSystemMessage()
        this.showNotification('✓ Conversation copied to clipboard!', 'success')

        if (btn) {
          const originalHtml = btn.innerHTML
          btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
            </svg>
          `
          btn.style.color = '#28a745'

          setTimeout(() => {
            btn.innerHTML = originalHtml
            btn.style.color = ''
            btn.disabled = false
            btn.style.opacity = ''
          }, 2000)
        }
      })
      .catch(err => {
        this.removeLastSystemMessage()
        this.showNotification('✗ Failed to copy to clipboard', 'error')
        if (btn) {
          btn.disabled = false
          btn.style.opacity = ''
        }
      })
  }

  // Full LLM diagnostic dump for localhost debugging
  copyLLMDiagnostic() {
    const btn = document.getElementById('diagnostic-btn')

    if (btn) {
      btn.disabled = true
      btn.textContent = 'Copying...'
    }

    // Get current editor content
    const currentHtml = window.monacoEditor ?
      window.monacoEditor.getValue() :
      (document.getElementById('html_content_textarea')?.value || '')

    // Build comprehensive diagnostic object
    const diagnostic = {
      _meta: {
        exportedAt: new Date().toISOString(),
        exportType: 'LLM_DIAGNOSTIC_FULL',
        purpose: 'Debug data for coding agent LLM analysis'
      },
      context: {
        pageContainerId: this.pageContainerIdValue,
        pageLabel: this.pageLabelValue,
        currentUserId: this.currentUserIdValue,
        conversationId: this.currentConversationId,
        currentModel: this.currentAIModel,
        isFirstMessage: this.isFirstMessage,
        isStreaming: !!this.streamingTextElement
      },
      conversationHistory: this.conversationHistory.map((msg, idx) => ({
        index: idx,
        role: msg.role,
        content: msg.content,
        ...(msg.name && { toolName: msg.name }),
        ...(msg.arguments && { toolArguments: msg.arguments }),
        ...(msg.images && msg.images.length > 0 && {
          hasImages: true,
          imageCount: msg.images.length,
          imageTypes: msg.images.map(img => img.media_type)
        })
      })),
      currentEditorState: {
        contentLength: currentHtml.length,
        lineCount: currentHtml.split('\n').length,
        content: currentHtml
      },
      uiState: {
        visibleMessageCount: this.chatHistoryTarget.querySelectorAll('.ai-message-group').length,
        systemMessageCount: this.chatHistoryTarget.querySelectorAll('.ai-message-group.system').length,
        hasWelcomeMessage: !!this.chatHistoryTarget.querySelector('.ai-welcome-message'),
        loadingVisible: this.loadingTarget.style.display !== 'none'
      },
      sessionStorage: {
        storedConversationId: sessionStorage.getItem(`current_conversation_${this.pageContainerIdValue}`)
      }
    }

    // Format as JSON with nice indentation
    const diagnosticJson = JSON.stringify(diagnostic, null, 2)

    navigator.clipboard.writeText(diagnosticJson)
      .then(() => {
        this.showNotification('✓ Full LLM diagnostic copied!', 'success')
        console.log('LLM Diagnostic Data:', diagnostic)

        if (btn) {
          btn.textContent = '✓ Copied!'
          btn.style.background = '#28a745'
          btn.style.borderColor = '#28a745'
          btn.style.color = 'white'

          setTimeout(() => {
            btn.textContent = 'Diagnostic'
            btn.style.background = ''
            btn.style.borderColor = ''
            btn.style.color = ''
            btn.disabled = false
          }, 2000)
        }
      })
      .catch(err => {
        this.showNotification('✗ Failed to copy diagnostic', 'error')
        console.error('Clipboard error:', err)
        if (btn) {
          btn.textContent = 'Diagnostic'
          btn.disabled = false
        }
      })
  }

  triggerFileSelect(event) {
    if (event) event.preventDefault()
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      this.showNotification('File too large. Please choose an image under 5MB.', 'error')
      this.fileInputTarget.value = ''
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      this.currentImage = {
        data: e.target.result.split(',')[1], // Base64 data
        media_type: file.type
      }
      this.imagePreviewImgTarget.src = e.target.result
      this.imagePreviewTarget.style.display = 'block'
    }
    reader.readAsDataURL(file)
  }

  clearImage(event) {
    if (event) event.preventDefault()
    this.fileInputTarget.value = ''
    this.currentImage = null
    this.imagePreviewTarget.style.display = 'none'
    this.imagePreviewImgTarget.src = ''
    // Also clear any asset URL from asset manager
    window.selectedAssetUrl = null
  }

  appendMapCompatibilityContext(message) {
    const mapIntentRegex = /\b(map|maps|marker|markers|pin|pins|radius|google\s*maps)\b/i
    if (!mapIntentRegex.test(message)) return message
    if (message.includes('Tangram map compatibility requirements:')) return message

    const compatibilityContext = [
      'Tangram map compatibility requirements:',
      '- Maps are auto-initialized by Tangram `global_script.js`; do NOT add `map_code.js`, `listing_map_code.js`, or Google Maps script tags.',
      '- Do NOT generate custom Google Maps JavaScript, map initialization scripts, MutationObservers, or map event listeners.',
      '- USER map containers must be inside the relevant `tg-section-id` and should use: `<div tg-addon-id="user-map" style="width: 100%; height: 400px;"></div>`.',
      '- LISTING map containers must be inside the relevant `tg-section-id` and should use: `<div tg-addon-id="listing-map" style="width: 100%; height: 400px;"></div>`.',
      '- Always set explicit map dimensions (minimum height 320px).',
      '- Keep map containers separate from `tg-import-type` elements.'
    ].join('\n')

    return `${message}\n\n${compatibilityContext}`
  }

  async send(event) {
    if (event) event.preventDefault()
    if (this.isStopping || (this.hasLoadingTarget && this.loadingTarget.style.display !== 'none')) return

    let message = this.inputTarget.value.trim()

    // Check for selected asset URL from asset manager
    const assetUrl = window.selectedAssetUrl

    // Allow sending if there's an image, asset URL, or text
    if (!message && !this.currentImage && !assetUrl) {
      this.inputTarget.classList.add('border-danger')
      setTimeout(() => this.inputTarget.classList.remove('border-danger'), 1000)
      return
    }

    // If there's an asset URL, append it to the message for the AI to use
    if (assetUrl) {
      const assetNote = `\n\n[Use this image URL: ${assetUrl}]`
      message = message ? message + assetNote : `Use this image: ${assetUrl}`
    }

    message = this.appendMapCompatibilityContext(message)
    this.ignoreIncomingStream = false
    this.isStopping = false
    this.activeStreamRequestId = this.generateStreamRequestId()

    // Disable UI
    this.setLoading(true)

    // Capture current image
    const imageToSend = this.currentImage

    // Add user message (show without the asset URL note for cleaner display)
    const displayMessage = this.inputTarget.value.trim() || (assetUrl ? 'Use this image' : '')
    const userMessageElement = this.addMessage('user', displayMessage, imageToSend, assetUrl)
    this.recordCheckpointForMessage(userMessageElement, this.getCurrentEditorHtml())
    this.conversationHistory.push({
      role: 'user',
      content: message,
      images: imageToSend ? [imageToSend] : []
    })

    // Clear input and image
    this.inputTarget.value = ''
    this.inputTarget.style.height = 'auto'
    this.clearImage()

    // Clear asset URL
    window.selectedAssetUrl = null

    if (this.currentAIModel === 'test' && this.isLocalhost) {
      this.addSystemMessage('Simulating test mode response...')
      try {
        await this.simulateTestModeResponse(message)
      } catch (error) {
        if (error.name === 'AbortError' || this.isStopping) return
        this.handleError(error.message)
      } finally {
        this.chatStartAbortController = null
      }
      return
    }

    this.addSystemMessage('Sending message...')

    try {
      this.subscribeToStream()
      this.chatStartAbortController = new AbortController()

      const response = await fetch(`/page_containers/${this.pageContainerIdValue}/code_assistant_chat_stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        signal: this.chatStartAbortController.signal,
        body: JSON.stringify({
          message: message,
          images: imageToSend ? [imageToSend] : [],
          history: this.conversationHistory.slice(0, -1), // Send history excluding the one we just added locally to avoid dupes if backend handles it? Actually backend expects history + current message separately.
          provider: this.currentAIModel,
          conversation_id: this.currentConversationId,
          stream_request_id: this.activeStreamRequestId
        })
      })

      if (!response.ok) {
        // Try to parse error message from response body
        let errorMessage = 'Failed to send message'
        let isCreditsError = response.status === 402
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch (e) {
          // If we can't parse JSON, check for common status codes
          if (isCreditsError) {
            errorMessage = "You've reached your monthly AI credit limit."
          }
        }

        // For credit limit errors, show special message with purchase link
        if (isCreditsError) {
          this.removeLastSystemMessage()
          this.handleCreditsError(errorMessage)
          this.setLoading(false)
          return
        }

        throw new Error(errorMessage)
      }

      const data = await response.json()
      if (data.request_id) {
        this.activeStreamRequestId = data.request_id
      }

      this.removeLastSystemMessage()

      // Update credits display using actual input credits from server (includes system prompt, history, tools)
      if (data.input_credits_used) {
        this.incrementCreditsUsed(data.input_credits_used)
      }

      if (data.conversation_id && !this.currentConversationId) {
        this.currentConversationId = data.conversation_id
        sessionStorage.setItem(`current_conversation_${this.pageContainerIdValue}`, this.currentConversationId)
      }

      if (data.error) {
        this.handleError(data.error)
      } else {
        this.setEditorReadOnly(true)
      }

    } catch (error) {
      if (error.name === 'AbortError' || this.isStopping) return
      this.handleError(error.message)
    } finally {
      this.chatStartAbortController = null
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.send()
    }
  }

  async stop() {
    if (this.isStopping) return
    this.isStopping = true
    this.ignoreIncomingStream = true
    const requestIdToStop = this.activeStreamRequestId
    this.abortPendingChatRequest()
    this.unsubscribeFromStream()
    this.handleStopSuccess()

    if (this.currentAIModel === 'test' && this.isLocalhost) {
      this.isStopping = false
      return
    }

    this.stopAbortController = new AbortController()
    try {
      const response = await fetch(this.stopUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        signal: this.stopAbortController.signal,
        body: JSON.stringify({
          request_id: requestIdToStop
        })
      })

      if (!response.ok) throw new Error('Failed to stop generation on server')
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Stop request failed:', error)
        this.showNotification('⚠️ Generation stopped locally, but server stop could not be confirmed.', 'warning')
      }
    } finally {
      this.stopAbortController = null
      this.isStopping = false
    }
  }

  handleStopSuccess() {
    if (this.streamingTextElement) {
      this.streamingTextElement.remove()
      this.streamingTextElement = null
    }

    this.resetActiveStreamState({ keepIgnoreIncoming: true })
    this.removeLastSystemMessage()
    this.addMessage('assistant', '🛑 Generation stopped by user.', null, null, { historyEntry: false })
    this.setLoading(false)
    this.setEditorReadOnly(false)
    this.showNotification('✓ Generation stopped', 'success')
  }

  subscribeToStream() {
    if (this.channel) this.channel.unsubscribe()

    if (!consumer) {
      console.error('ActionCable consumer not available')
      this.handleError('Connection error. Please refresh the page.')
      return
    }

    this.channel = consumer.subscriptions.create(
      {
        channel: "CodeAssistantChatChannel",
        page_container_id: this.pageContainerIdValue,
        user_id: this.currentUserIdValue
      },
      {
        connected: () => {
          console.log('Connected to CodeAssistantChatChannel')
        },
        disconnected: () => {
          if (this.isStopping || this.ignoreIncomingStream) return
          console.warn('Disconnected from CodeAssistantChatChannel')
          // Only handle as error if we were still loading
          if (this.loadingTarget.style.display !== 'none') {
            this.handleConnectionLost()
          }
        },
        rejected: () => {
          console.error('Subscription rejected for CodeAssistantChatChannel')
          this.handleError('Connection rejected. Please refresh the page.')
        },
        received: (data) => this.handleStreamMessage(data)
      }
    )
  }

  handleConnectionLost() {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()

    if (this.streamingTextElement) {
      // Preserve any partial content
      const partialText = this.streamingTextElement.querySelector('.streaming-text-content')?.textContent
      this.streamingTextElement.classList.remove('streaming-text')
      if (partialText && partialText.trim()) {
        this.conversationHistory.push({ role: 'assistant', content: partialText + '\n\n[Connection lost - response incomplete]' })
      } else {
        this.streamingTextElement.remove()
      }
      this.streamingTextElement = null
    }

    this.removeLastSystemMessage()
    this.addSystemMessage('⚠️ Connection lost. Attempting to reconnect...')
    this.setLoading(false)
    this.setEditorReadOnly(false)

    // Try to reconnect after a short delay
    setTimeout(() => {
      this.removeLastSystemMessage()
      this.showNotification('⚠️ Connection lost. Your progress has been saved.', 'warning')
    }, 2000)
  }

  handleStreamMessage(data) {
    if (this.ignoreIncomingStream) return
    if (this.activeStreamRequestId && (!data.request_id || data.request_id !== this.activeStreamRequestId)) {
      return
    }

    switch (data.type) {
      case 'token':
        this.handleToken(data.token)
        break
      case 'tool_call':
        this.handleToolCall(data.name, data.arguments)
        break
      case 'thinking':
        // AI is processing - just update timeout, no UI change needed
        // This keeps the connection alive during long API calls
        break
      case 'stopped':
        this.handleStopped(data.message)
        break
      case 'done':
        this.handleDone(data)
        break
      case 'error':
        this.handleError(data.error)
        break
      case 'fallback':
        this.handleFallback(data.message)
        break
    }

    this.updateTimeoutDetection()
  }

  handleToken(token, options = {}) {
    if (!this.streamingTextElement) {
      this.startStreamingTextMessage()
    }

    const contentDiv = this.streamingTextElement.querySelector('.streaming-text-content')
    contentDiv.textContent += token
    this.scrollToBottom()

    // Increment credits used display (1 credit per character)
    if (options.countCredits !== false) {
      this.incrementCreditsUsed(token.length)
    }
  }

  incrementCreditsUsed(amount = 1) {
    if (this.hasCreditsUsedTarget) {
      const currentText = this.creditsUsedTarget.textContent.replace(/,/g, '')
      const currentValue = parseInt(currentText) || 0
      const newValue = currentValue - amount
      this.creditsUsedTarget.textContent = newValue.toLocaleString()
    }
  }

  handleToolCall(name, args) {
    // Finalize any current streaming text before the tool call
    // This ensures text after the tool call appears as a new message
    if (this.streamingTextElement) {
      this.streamingTextElement.classList.remove('streaming-text')
      const finalText = this.streamingTextElement.querySelector('.streaming-text-content').textContent

      if (finalText.trim()) {
        this.conversationHistory.push({ role: 'assistant', content: finalText })
      }

      this.streamingTextElement = null
    }

    this.addToolCallMessage(name, args)
    this.conversationHistory.push({
      role: 'tool',
      name: name,
      arguments: args
    })

    this.setEditorReadOnly(true)
  }

  handleDone(data) {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()
    let completedAssistantElement = null

    if (this.streamingTextElement) {
      completedAssistantElement = this.streamingTextElement
      this.streamingTextElement.classList.remove('streaming-text')
      const finalText = this.streamingTextElement.querySelector('.streaming-text-content').textContent

      if (finalText.trim()) {
        this.conversationHistory.push({ role: 'assistant', content: finalText })
      }

      this.streamingTextElement = null
    } else if (!data.warning) {
      completedAssistantElement = this.addMessage('assistant', 'Done.')
      this.conversationHistory.push({ role: 'assistant', content: 'Done.' })
    }

    // Show warning if max iterations was reached
    if (data.warning) {
      this.addMessage('assistant', '⚠️ ' + data.warning)
      this.conversationHistory.push({ role: 'assistant', content: data.warning })
      this.showNotification('⚠️ ' + data.warning, 'warning')
    }

    if (data.updated_html) {
      this.updateEditor(data.updated_html, completedAssistantElement)
    }

    this.setLoading(false)
    this.setEditorReadOnly(false)
  }

  handleError(error) {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()
    if (this.streamingTextElement) {
      this.streamingTextElement.remove()
      this.streamingTextElement = null
    }

    this.addMessage('assistant', '⚠️ ' + error, null, null, { historyEntry: false })
    this.setLoading(false)
    this.setEditorReadOnly(false)
    this.showNotification('✗ ' + error, 'error')
  }

  handleCreditsError(errorMessage) {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()
    if (this.streamingTextElement) {
      this.streamingTextElement.remove()
      this.streamingTextElement = null
    }

    // Create message with purchase link
    const messageHtml = `
      <div class="ai-message-group assistant" style="opacity: 0;">
        <div class="ai-message-bubble assistant">
          <div class="message-text">
            ⚠️ ${this.escapeHtml(errorMessage)}
            <div style="margin-top: 12px;">
              <a href="${this.upgradeUrlValue}" class="btn btn-sm btn-primary" style="text-decoration: none;">
                Purchase More Credits
              </a>
            </div>
          </div>
        </div>
      </div>
    `
    this.chatHistoryTarget.insertAdjacentHTML('beforeend', messageHtml)

    const lastMessage = this.chatHistoryTarget.lastElementChild
    requestAnimationFrame(() => {
      lastMessage.style.opacity = '1'
    })

    this.scrollToBottom()
    this.setEditorReadOnly(false)
    this.showNotification('✗ Credit limit reached', 'error')
  }

  handleFallback(message) {
    // Show notification about fallback
    this.showNotification('⚡ ' + message, 'warning')
    this.addSystemMessage(message)

    // Update model switcher UI to reflect we're now using fast mode
    this.currentAIModel = 'openai'
    this.updateModelButton()
  }

  // Helper methods
  generateStreamRequestId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }

  resetActiveStreamState(options = {}) {
    const keepIgnoreIncoming = options.keepIgnoreIncoming === true

    this.activeStreamRequestId = null
    this.isStopping = false
    if (!keepIgnoreIncoming) {
      this.ignoreIncomingStream = false
    }
  }

  abortPendingChatRequest() {
    if (this.chatStartAbortController) {
      this.chatStartAbortController.abort()
      this.chatStartAbortController = null
    }
  }

  abortPendingStopRequest() {
    if (this.stopAbortController) {
      this.stopAbortController.abort()
      this.stopAbortController = null
    }
  }

  unsubscribeFromStream() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.loadingTarget.style.display = 'block'
      this.sendButtonTarget.style.display = 'none'
      this.stopButtonTarget.style.display = 'block'
      this.inputTarget.disabled = true
      this.startTimeoutDetection()
    } else {
      this.loadingTarget.style.display = 'none'
      this.sendButtonTarget.style.display = 'block'
      this.stopButtonTarget.style.display = 'none'
      this.inputTarget.disabled = false
      this.inputTarget.focus()
      this.clearTimeoutDetection()
    }
    this.scrollToBottom()
  }

  addMessage(role, message, image = null, assetUrl = null, options = {}) {
    if (this.isFirstMessage && role === 'user') {
      if (this.hasWelcomeMessageTarget) this.welcomeMessageTarget.remove()
      this.isFirstMessage = false
    }

    let contentHtml = ''

    // Show base64 image (from file upload)
    if (image) {
      const imgSrc = `data:${image.media_type};base64,${image.data}`
      contentHtml += `
        <div class="message-image" style="margin-bottom: 8px;">
          <img src="${imgSrc}" style="max-width: 100%; max-height: 200px; border-radius: 6px; border: 1px solid #dee2e6;">
        </div>
      `
    }

    // Show asset image (from asset manager)
    if (assetUrl) {
      contentHtml += `
        <div class="message-image" style="margin-bottom: 8px;">
          <img src="${assetUrl}" style="max-width: 100%; max-height: 200px; border-radius: 6px; border: 1px solid #dee2e6;" onerror="this.style.display='none'">
        </div>
      `
    }

    if (message) {
      contentHtml += `<div class="message-text">${this.escapeHtml(message)}</div>`
    }

    const messageHtml = `
      <div class="ai-message-group ${role}" style="opacity: 0;">
        <div class="ai-message-bubble ${role}">
          ${contentHtml}
        </div>
      </div>
    `
    this.chatHistoryTarget.insertAdjacentHTML('beforeend', messageHtml)

    const lastMessage = this.chatHistoryTarget.lastElementChild
    lastMessage.dataset.historyTracked = options.historyEntry === false ? 'false' : 'true'
    if (options.enableRevert !== false) {
      this.registerMessageForRevert(lastMessage)
    }
    requestAnimationFrame(() => {
      lastMessage.style.opacity = '1'
    })

    this.scrollToBottom()
    return lastMessage
  }

  addToolCallMessage(name, args, options = {}) {
    // Ensure args is an object to prevent crashes
    const safeArgs = args || {}
    const info = this.getToolInfo(name, safeArgs)

    const detailsHtml = info.details ? `<div style="font-size: 0.85em; opacity: 0.8; font-weight: 400; margin-top: 2px;">${info.details}</div>` : ''
    const html = `
      <div class="ai-message-group tool" style="opacity: 0;">
        <div class="ai-message-bubble tool">
          <div class="tool-call-header">
            <span class="tool-icon"></span>
            <span class="tool-name">
              <div style="font-size: 1em; font-weight: 600;">${info.action}</div>
              ${detailsHtml}
            </span>
          </div>
        </div>
      </div>
    `
    this.chatHistoryTarget.insertAdjacentHTML('beforeend', html)

    const newElement = this.chatHistoryTarget.lastElementChild
    newElement.dataset.historyTracked = options.historyEntry === false ? 'false' : 'true'
    if (options.enableRevert !== false) {
      this.registerMessageForRevert(newElement)
    }

    requestAnimationFrame(() => {
      newElement.style.opacity = '1'
    })
    this.scrollToBottom()
    return newElement
  }

  startStreamingTextMessage() {
    if (this.isFirstMessage) {
      if (this.hasWelcomeMessageTarget) this.welcomeMessageTarget.remove()
      this.isFirstMessage = false
    }

    const html = `
      <div class="ai-message-group assistant streaming-text" style="opacity: 0;">
        <div class="ai-message-bubble assistant">
          <div class="message-text streaming-text-content"></div>
        </div>
      </div>
    `
    this.chatHistoryTarget.insertAdjacentHTML('beforeend', html)
    this.streamingTextElement = this.chatHistoryTarget.lastElementChild
    this.registerMessageForRevert(this.streamingTextElement)

    // Store local reference for the callback to avoid race condition
    // where handleDone/handleToolCall sets this.streamingTextElement to null
    // before the requestAnimationFrame callback runs
    const element = this.streamingTextElement
    requestAnimationFrame(() => {
      element.style.opacity = '1'
    })
    this.scrollToBottom()
    return this.streamingTextElement
  }

  addSystemMessage(message) {
    const html = `
      <div class="ai-message-group system" style="opacity: 0;">
        <div class="ai-message-bubble system">
          <div class="message-text" style="font-size: 0.85em; font-style: italic; color: #6c757d;">
            ${this.escapeHtml(message)}
          </div>
        </div>
      </div>
    `
    this.chatHistoryTarget.insertAdjacentHTML('beforeend', html)

    const newElement = this.chatHistoryTarget.lastElementChild

    requestAnimationFrame(() => {
      newElement.style.opacity = '1'
    })
    this.scrollToBottom()
  }

  removeLastSystemMessage() {
    const systemMessages = this.chatHistoryTarget.querySelectorAll('.ai-message-group.system')
    if (systemMessages.length > 0) {
      const last = systemMessages[systemMessages.length - 1]
      last.style.opacity = '0'
      setTimeout(() => last.remove(), 200)
    }
  }

  scrollToBottom() {
    const element = this.chatHistoryTarget
    // Simple scroll to bottom, logic for "near bottom" can be added if needed
    element.scrollTop = element.scrollHeight
  }

  getCurrentEditorHtml() {
    if (window.monacoEditor) {
      return window.monacoEditor.getValue()
    }

    return document.getElementById('html_content_textarea')?.value || ''
  }

  resetRevertTimeline() {
    this.messageIndexCounter = 0
    this.revertCheckpoints = []

    const initialHtml = this.getCurrentEditorHtml()
    this.lastKnownEditorHtml = initialHtml
  }

  registerMessageForRevert(messageElement) {
    if (!messageElement || messageElement.classList.contains('system')) return

    if (!messageElement.dataset.messageIndex) {
      this.messageIndexCounter += 1
      messageElement.dataset.messageIndex = String(this.messageIndexCounter)
    }

    if (!messageElement.classList.contains('user')) return

    const messageBubble = messageElement.querySelector('.ai-message-bubble')
    if (!messageBubble) return

    let actionsContainer = messageBubble.querySelector('.ai-message-actions')
    if (!actionsContainer) {
      actionsContainer = document.createElement('div')
      actionsContainer.className = 'ai-message-actions'
      messageBubble.appendChild(actionsContainer)
    }

    let revertButton = actionsContainer.querySelector('.ai-revert-btn')
    if (!revertButton) {
      revertButton = document.createElement('button')
      revertButton.type = 'button'
      revertButton.className = 'ai-revert-btn'
      revertButton.textContent = '← Revert'
      actionsContainer.appendChild(revertButton)
    }

    const messageIndex = parseInt(messageElement.dataset.messageIndex, 10)
    revertButton.dataset.messageIndex = String(messageIndex)
    revertButton.onclick = (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.revertToMessageIndex(messageIndex)
    }

    this.updateRevertButtonState(revertButton, messageIndex)
  }

  updateRevertButtonState(button, messageIndex) {
    if (!button) return

    const checkpoint = this.findCheckpointForMessageIndex(messageIndex)
    const actionsContainer = button.closest('.ai-message-actions')

    if (!checkpoint) {
      button.style.display = 'none'
      if (actionsContainer) actionsContainer.style.display = 'none'
      return
    }

    if (actionsContainer) actionsContainer.style.display = 'flex'
    button.style.display = 'inline-flex'
    button.disabled = false
    button.title = 'Revert the page to before this message'
  }

  findCheckpointForMessageIndex(messageIndex) {
    return this.revertCheckpoints.find((checkpoint) => checkpoint.messageIndex === messageIndex) || null
  }

  refreshRevertButtons() {
    const buttons = this.chatHistoryTarget.querySelectorAll('.ai-revert-btn')
    buttons.forEach((button) => {
      const messageIndex = parseInt(button.dataset.messageIndex || '0', 10)
      this.updateRevertButtonState(button, messageIndex)
    })
  }

  recordCheckpointForMessage(messageElement, html) {
    if (!messageElement || typeof html !== 'string') return
    if (!messageElement.dataset.messageIndex) return

    const messageIndex = parseInt(messageElement.dataset.messageIndex, 10)
    if (Number.isNaN(messageIndex)) return

    this.revertCheckpoints = this.revertCheckpoints.filter(
      (checkpoint) => checkpoint.messageIndex !== messageIndex
    )

    this.revertCheckpoints.push({ messageIndex: messageIndex, html: html })
    this.lastKnownEditorHtml = html
    messageElement.classList.add('has-revert-checkpoint')
    this.refreshRevertButtons()
  }

  revertToMessageIndex(messageIndex) {
    const checkpoint = this.findCheckpointForMessageIndex(messageIndex)
    if (!checkpoint) {
      this.showNotification('No saved state is available for this message yet.', 'warning')
      return
    }

    const html = checkpoint.html || ''
    const textarea = document.querySelector('#html_content_textarea')
    const draftTextarea = document.querySelector('#draft_html_content_textarea')

    if (window.monacoEditor) {
      const currentPosition = window.monacoEditor.getPosition()
      window.monacoEditor.setValue(html)
      if (currentPosition) {
        try {
          window.monacoEditor.setPosition(currentPosition)
        } catch (_error) {
          // Position can be out of bounds after large content changes.
        }
      }
    } else if (textarea) {
      textarea.value = html
    }

    if (textarea) textarea.value = html
    if (draftTextarea) draftTextarea.value = html
    this.lastKnownEditorHtml = html

    if (typeof window.hasUnsavedChanges !== 'undefined') {
      window.hasUnsavedChanges = true
    }

    if ($('#preview-mode-btn').hasClass('active') && typeof window.updatePreview === 'function') {
      setTimeout(() => window.updatePreview(), 50)
    }

    this.deleteMessagesAfter(messageIndex)
    this.showNotification('✓ Reverted page and removed this message and newer messages', 'success')
  }

  deleteMessagesAfter(messageIndex) {
    const messageGroups = this.chatHistoryTarget.querySelectorAll('.ai-message-group:not(.system)')
    messageGroups.forEach((messageElement) => {
      const index = parseInt(messageElement.dataset.messageIndex || '0', 10)
      if (!Number.isNaN(index) && index >= messageIndex) {
        messageElement.remove()
      }
    })

    this.revertCheckpoints = this.revertCheckpoints.filter(
      (checkpoint) => checkpoint.messageIndex < messageIndex
    )

    const remainingHistoryCount = this.chatHistoryTarget.querySelectorAll('.ai-message-group:not(.system)[data-history-tracked="true"]').length
    this.conversationHistory = this.conversationHistory.slice(0, remainingHistoryCount)
    const remainingMessageIndices = Array.from(
      this.chatHistoryTarget.querySelectorAll('.ai-message-group:not(.system)[data-message-index]')
    )
      .map((messageElement) => parseInt(messageElement.dataset.messageIndex || '0', 10))
      .filter((index) => !Number.isNaN(index))

    this.messageIndexCounter = remainingMessageIndices.length > 0 ? Math.max(...remainingMessageIndices) : 0
    this.refreshRevertButtons()
  }

  setEditorReadOnly(readOnly, message = '') {
    // This depends on how monaco is exposed globally. 
    // Ideally this should be an event or another controller interaction.
    // For now, keeping the global window calls as fallback.
    if (window.setEditorReadOnly) {
      window.setEditorReadOnly(readOnly, message)
    }
  }

  updateEditor(html, messageElement = null) {
    this.addSystemMessage('✓ Code updated in editor')

    const textarea = document.querySelector('#html_content_textarea')

    if (window.monacoEditor) {
      const currentPosition = window.monacoEditor.getPosition()
      window.monacoEditor.setValue(html)
      if (currentPosition) {
        window.monacoEditor.setPosition(currentPosition)
      }
      setTimeout(() => {
        window.monacoEditor.getAction('editor.action.formatDocument').run()
      }, 100)
    } else {
      if (textarea) textarea.value = html
    }

    if (textarea) textarea.value = html
    this.lastKnownEditorHtml = html

    let checkpointMessageElement = messageElement
    if (!checkpointMessageElement) {
      const assistantMessages = this.chatHistoryTarget.querySelectorAll('.ai-message-group.assistant')
      checkpointMessageElement = assistantMessages[assistantMessages.length - 1] || null
    }

    this.recordCheckpointForMessage(checkpointMessageElement, html)
    setTimeout(() => this.removeLastSystemMessage(), 3000)
  }

  showNotification(message, type = 'success') {
    // Simple toast implementation
    const colors = { success: '#28a745', error: '#dc3545', warning: '#ffc107' }
    const color = colors[type] || colors.success

    const existing = document.getElementById('simple-copy-notification')
    if (existing) existing.remove()

    const notification = document.createElement('div')
    notification.id = 'simple-copy-notification'
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: color,
      color: 'white',
      padding: '12px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: '500',
      opacity: '0',
      transition: 'opacity 0.3s ease'
    })
    notification.textContent = message
    document.body.appendChild(notification)

    requestAnimationFrame(() => notification.style.opacity = '1')

    setTimeout(() => {
      notification.style.opacity = '0'
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getToolInfo(name, args) {
    if (!name) return { action: 'Working', details: 'just a moment' }

    // User-friendly action descriptions
    switch (name) {
      case 'insert_lines': return { action: 'Adding content', details: 'updating your page' }
      case 'delete_lines': return { action: 'Removing content', details: 'cleaning things up' }
      case 'replace_lines': return { action: 'Updating code', details: 'making changes' }
      case 'view_code': return { action: 'Reviewing code', details: 'looking things over' }
      case 'search_code': return { action: 'Searching', details: args.query ? `looking for "${args.query}"` : 'scanning the code' }
      case 'get_assets': return { action: 'Loading assets', details: 'retrieving uploaded images' }
      case 'get_platform_config': return { action: 'Loading config', details: 'checking page settings' }
      case 'get_design_system': return { action: 'Loading styles', details: 'checking design tokens' }
      case 'get_global_styles': return { action: 'Loading styles', details: 'checking global CSS' }
      case 'get_navbar_html': return { action: 'Loading navbar', details: 'checking navigation' }
      case 'create_section': return { action: 'Creating section', details: args.title ? `adding "${args.title}"` : 'setting up new section' }
      case 'add_data_source': return { action: 'Connecting data', details: 'linking content source' }
      case 'add_field_connection': return { action: 'Connecting field', details: args.field_slug ? `linking "${args.field_slug}"` : 'setting up field' }
      case 'add_page_filter': return { action: 'Adding filter', details: 'setting up filtering' }
      default: return { action: 'Working', details: 'processing your request' }
    }
  }

  async toggleModel(event) {
    event.preventDefault()
    const btn = this.modelSwitcherTarget
    const modes = this.availableModels()
    const currentIndex = modes.indexOf(this.normalizeModel(this.currentAIModel))
    const newModel = modes[(currentIndex + 1) % modes.length]

    // Disable button during update
    btn.disabled = true
    const originalText = btn.textContent
    btn.textContent = 'Switching...'

    try {
      // Update the conversation's provider on the backend for real providers
      if (this.currentConversationId && newModel !== 'test') {
        const response = await fetch(`/page_containers/${this.pageContainerIdValue}/code_assistant_conversations/${this.currentConversationId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ provider: newModel })
        })

        if (!response.ok) {
          throw new Error('Failed to update conversation provider')
        }
      }

      this.currentAIModel = newModel
      this.updateModelButton()
      this.showNotification(`✓ Switched to ${this.modelLabel(this.currentAIModel)} Mode`)
    } catch (error) {
      console.error('Error switching model:', error)
      this.showNotification('✗ Failed to switch mode. Try starting a new chat.', 'error')
      // Revert UI
      btn.textContent = originalText
    } finally {
      btn.disabled = false
    }
  }

  // Timeout detection - 5 minutes without any activity triggers auto-recovery
  // This must be longer than the backend API timeout (120s read + 60s open = 180s)
  // to allow for legitimate long-running AI operations
  startTimeoutDetection() {
    this.lastTokenTime = Date.now()
    this.timeoutInterval = setInterval(() => {
      const elapsed = Date.now() - this.lastTokenTime
      if (elapsed > 300000) { // 5 minutes (300 seconds) without activity
        this.handleTimeout()
      }
    }, 10000) // Check every 10 seconds
  }

  clearTimeoutDetection() {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval)
      this.timeoutInterval = null
    }
  }

  updateTimeoutDetection() {
    this.lastTokenTime = Date.now()
  }

  handleTimeout() {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()

    // Clean up streaming element if exists
    if (this.streamingTextElement) {
      this.streamingTextElement.remove()
      this.streamingTextElement = null
    }

    this.removeLastSystemMessage()
    this.addMessage('assistant', '⚠️ Request timed out. Please try again.', null, null, { historyEntry: false })
    this.setLoading(false)
    this.setEditorReadOnly(false)
    this.showNotification('⚠️ Request timed out - no response received', 'warning')

    // Unsubscribe from the channel
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
    }
  }

  handleStopped(message) {
    this.clearTimeoutDetection()
    this.resetActiveStreamState()

    if (this.streamingTextElement) {
      this.streamingTextElement.remove()
      this.streamingTextElement = null
    }

    this.removeLastSystemMessage()
    this.addMessage('assistant', message || '🛑 Generation stopped.', null, null, { historyEntry: false })
    this.setLoading(false)
    this.setEditorReadOnly(false)
    this.showNotification('✓ Generation stopped', 'success')
  }

  // ===== Asset Manager Methods =====

  async openAssetManager(event) {
    if (event) event.preventDefault()

    // Show the modal
    const modal = document.getElementById('asset-manager-modal')
    if (modal) {
      $(modal).modal('show')
      this.loadAssets()
      this.initAssetDropZone()
    }
  }

  async loadAssets() {
    const loading = document.getElementById('asset-manager-loading')
    const grid = this.hasAssetGridTarget ? this.assetGridTarget : document.getElementById('asset-manager-grid')
    const empty = this.hasAssetEmptyTarget ? this.assetEmptyTarget : document.getElementById('asset-manager-empty')

    if (loading) loading.style.display = 'block'
    if (grid) grid.style.display = 'none'
    if (empty) empty.style.display = 'none'

    try {
      const response = await fetch(this.assetsUrlValue, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) throw new Error('Failed to load assets')

      const data = await response.json()

      if (loading) loading.style.display = 'none'

      if (data.assets && data.assets.length > 0) {
        this.renderAssets(data.assets)
        if (grid) grid.style.display = 'grid'
        if (empty) empty.style.display = 'none'
      } else {
        if (grid) grid.style.display = 'none'
        if (empty) empty.style.display = 'block'
      }
    } catch (error) {
      console.error('Error loading assets:', error)
      if (loading) loading.style.display = 'none'
      this.showNotification('Failed to load assets', 'error')
    }
  }

  renderAssets(assets) {
    const grid = this.hasAssetGridTarget ? this.assetGridTarget : document.getElementById('asset-manager-grid')
    if (!grid) return

    grid.innerHTML = assets.map(asset => `
      <div class="asset-card" data-asset-id="${asset.id}" data-asset-url="${asset.url || ''}" data-action="click->page-editor-agent#selectAsset">
        <button type="button" class="asset-card-delete" data-action="click->page-editor-agent#deleteAsset" data-asset-id="${asset.id}" title="Delete asset">×</button>
        <img src="${asset.thumbnail_url || asset.url}" alt="${this.escapeHtml(asset.name)}" class="asset-card-image" onerror="this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22%3E%3Crect fill=%22%23ddd%22 width=%22100%22 height=%22100%22/%3E%3Ctext x=%2250%22 y=%2250%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23999%22 font-size=%2212%22%3ENo preview%3C/text%3E%3C/svg%3E'">
        <div class="asset-card-info">
          <div class="asset-card-name" title="${this.escapeHtml(asset.name)}">${this.escapeHtml(asset.name)}</div>
          <div class="asset-card-size">${this.formatFileSize(asset.byte_size)}</div>
        </div>
      </div>
    `).join('')
  }

  selectAsset(event) {
    // Don't select if clicking the delete button
    if (event.target.closest('.asset-card-delete')) return

    const card = event.currentTarget
    const url = card.dataset.assetUrl

    if (url) {
      // Insert the URL into the input field
      const currentValue = this.inputTarget.value
      const cursorPos = this.inputTarget.selectionStart
      const textBefore = currentValue.substring(0, cursorPos)
      const textAfter = currentValue.substring(cursorPos)

      // Add URL with a space before if needed
      const prefix = textBefore && !textBefore.endsWith(' ') && !textBefore.endsWith('\n') ? ' ' : ''
      const suffix = textAfter && !textAfter.startsWith(' ') && !textAfter.startsWith('\n') ? ' ' : ''

      this.inputTarget.value = textBefore + prefix + url + suffix + textAfter

      // Close the modal
      const modal = document.getElementById('asset-manager-modal')
      if (modal) $(modal).modal('hide')

      // Focus the input
      this.inputTarget.focus()

      // Show confirmation
      this.showNotification('✓ Image URL added to message', 'success')
    }
  }

  async deleteAsset(event) {
    event.preventDefault()
    event.stopPropagation()

    const assetId = event.currentTarget.dataset.assetId
    if (!assetId) return

    if (!confirm('Are you sure you want to delete this asset?')) return

    const card = event.currentTarget.closest('.asset-card')
    if (card) card.classList.add('asset-uploading')

    try {
      const response = await fetch(`${this.assetsUrlValue}/${assetId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) throw new Error('Failed to delete asset')

      // Remove the card with animation
      if (card) {
        card.style.opacity = '0'
        card.style.transform = 'scale(0.8)'
        setTimeout(() => {
          card.remove()
          // Check if grid is now empty
          const grid = this.hasAssetGridTarget ? this.assetGridTarget : document.getElementById('asset-manager-grid')
          const empty = this.hasAssetEmptyTarget ? this.assetEmptyTarget : document.getElementById('asset-manager-empty')
          if (grid && grid.children.length === 0) {
            grid.style.display = 'none'
            if (empty) empty.style.display = 'block'
          }
        }, 200)
      }

      this.showNotification('✓ Asset deleted', 'success')
    } catch (error) {
      console.error('Error deleting asset:', error)
      if (card) card.classList.remove('asset-uploading')
      this.showNotification('Failed to delete asset', 'error')
    }
  }

  triggerAssetUpload(event) {
    if (event) event.preventDefault()
    const input = this.hasAssetUploadInputTarget ? this.assetUploadInputTarget : document.getElementById('asset-upload-input')
    if (input) input.click()
  }

  async handleAssetUpload(event) {
    const file = event.target.files[0]
    if (!file) return

    // Reset the input
    event.target.value = ''

    await this.uploadAssetFile(file)
  }

  async uploadAssetFile(file) {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      this.showNotification('Invalid file type. Please use an image file (JPEG, PNG, GIF, WebP, or SVG).', 'error')
      return
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      this.showNotification('File too large. Please choose an image under 10MB.', 'error')
      return
    }

    // Show uploading state
    const grid = this.hasAssetGridTarget ? this.assetGridTarget : document.getElementById('asset-manager-grid')
    const empty = this.hasAssetEmptyTarget ? this.assetEmptyTarget : document.getElementById('asset-manager-empty')

    // Create a temporary placeholder card
    const placeholderHtml = `
      <div class="asset-card asset-uploading" id="asset-uploading-placeholder">
        <div style="width: 100%; height: 100px; background: #eee; display: flex; align-items: center; justify-content: center;">
          <span style="font-size: 12px; color: #666;">Uploading...</span>
        </div>
        <div class="asset-card-info">
          <div class="asset-card-name">${this.escapeHtml(file.name)}</div>
          <div class="asset-card-size">${this.formatFileSize(file.size)}</div>
        </div>
      </div>
    `

    if (empty) empty.style.display = 'none'
    if (grid) {
      grid.style.display = 'grid'
      grid.insertAdjacentHTML('afterbegin', placeholderHtml)
    }

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(this.assetsUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      // Remove placeholder
      const placeholder = document.getElementById('asset-uploading-placeholder')
      if (placeholder) placeholder.remove()

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to upload asset')
      }

      const data = await response.json()

      // Add the new asset to the grid
      if (data.asset && grid) {
        const newCardHtml = `
          <div class="asset-card" data-asset-id="${data.asset.id}" data-asset-url="${data.asset.url || ''}" data-action="click->page-editor-agent#selectAsset" style="opacity: 0; transform: scale(0.8); transition: all 0.2s ease;">
            <button type="button" class="asset-card-delete" data-action="click->page-editor-agent#deleteAsset" data-asset-id="${data.asset.id}" title="Delete asset">×</button>
            <img src="${data.asset.thumbnail_url || data.asset.url}" alt="${this.escapeHtml(data.asset.name)}" class="asset-card-image" onerror="this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22%3E%3Crect fill=%22%23ddd%22 width=%22100%22 height=%22100%22/%3E%3Ctext x=%2250%22 y=%2250%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23999%22 font-size=%2212%22%3ENo preview%3C/text%3E%3C/svg%3E'">
            <div class="asset-card-info">
              <div class="asset-card-name" title="${this.escapeHtml(data.asset.name)}">${this.escapeHtml(data.asset.name)}</div>
              <div class="asset-card-size">${this.formatFileSize(data.asset.byte_size)}</div>
            </div>
          </div>
        `
        grid.insertAdjacentHTML('afterbegin', newCardHtml)

        // Animate in
        const newCard = grid.firstElementChild
        requestAnimationFrame(() => {
          newCard.style.opacity = '1'
          newCard.style.transform = 'scale(1)'
        })
      }

      this.showNotification('✓ Asset uploaded successfully', 'success')
    } catch (error) {
      console.error('Error uploading asset:', error)

      // Remove placeholder
      const placeholder = document.getElementById('asset-uploading-placeholder')
      if (placeholder) placeholder.remove()

      // Check if grid is now empty
      if (grid && grid.children.length === 0) {
        grid.style.display = 'none'
        if (empty) empty.style.display = 'block'
      }

      this.showNotification(error.message || 'Failed to upload asset', 'error')
    }
  }

  initAssetDropZone() {
    const dropZone = document.getElementById('asset-drop-zone')
    if (!dropZone || dropZone._dropInitialized) return
    dropZone._dropInitialized = true

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.add('active')
    })

    dropZone.addEventListener('dragenter', (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.add('active')
    })

    dropZone.addEventListener('dragleave', (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.remove('active')
    })

    dropZone.addEventListener('drop', (e) => {
      e.preventDefault()
      e.stopPropagation()
      dropZone.classList.remove('active')

      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.uploadAssetFile(files[0])
      }
    })
  }

  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }
}
